import React, { useEffect, useState, useRef } from "react";
import { Form, Row, Col, Container, Card } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { addpackage, editpackage } from "../../store/slices/package";
import { getPlace } from "../../store/slices/place";
import { getpackageCategory } from "../../store/slices/packagecategory";
import { uploadImage } from "../../utils/helpers/fileupload";
import { toast } from "react-toastify";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { getuserProfile } from "../../store/slices/auth";

export default function CopyPackageStep({
  form,
  setForm,
  packageId,
  setPackageId,
  setDurationDays,
  nextStep,
}) {
  const dispatch = useDispatch();

  const user = JSON.parse(localStorage.getItem("user"));
  const token = user?.data?.accessToken;

  const [errors, setErrors] = useState({});
  const { placeList = [] } = useSelector((state) => state.place);
  const { packageCategoryList = [] } = useSelector(
    (state) => state.packageCategory,
  );

  const [images, setImages] = useState([]);
  const hasFetchedProfile = useRef(false);

  const { profile } = useSelector((state) => state.auth);

  useEffect(() => {
    if (!packageId && !profile && !hasFetchedProfile.current) {
      hasFetchedProfile.current = true;
      dispatch(getuserProfile());
    }
  }, [dispatch, packageId, profile]);

  useEffect(() => {
    if (!packageId && profile?.organization) {
      const org = profile.organization;
      setForm((prev) => ({
        ...prev,
        inclusions: prev.inclusions || org.inclusion || "",
        exclusions: prev.exclusions || org.exclusion || "",
        paymentpolicy: prev.paymentpolicy || org.paymentPolicy || "",
      }));
    }
  }, [profile, packageId]);

  useEffect(() => {
    dispatch(getPlace());
    dispatch(getpackageCategory());
  }, [dispatch]);

  const placeOptions = placeList.map((place) => ({
    value: place._id,
    label: place.name,
  }));

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const MAX_SIZE = 2 * 1024 * 1024;

    let errorMessages = [];

    const validImages = files.filter((file) => {
      if (!file.type.startsWith("image/")) {
        errorMessages.push(`${file.name} is not a valid image file.`);
        return false;
      }

      if (file.size > MAX_SIZE) {
        errorMessages.push(`${file.name} exceeds 2MB size limit.`);
        return false;
      }

      return true;
    });

    if (errorMessages.length > 0) {
      setErrors((prev) => ({
        ...prev,
        images: errorMessages.join(" "),
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        images: "",
      }));
    }

    if (validImages.length > 0) {
      const newImages = validImages.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));

      setImages((prev) => [...prev, ...newImages]);
    }

    e.target.value = null;
  };

  const removeImage = (index) => {
    setImages((prev) => {
      const updated = [...prev];

      URL.revokeObjectURL(updated[index].preview);

      updated.splice(index, 1);
      return updated;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let error = "";

    if (name === "title") {
      if (!/^[a-zA-Z0-9][\s\S]*$/.test(value)) {
        return;
      } else if (value.trim() === "") {
        error = "Please enter package name";
      } else if (value.trim().length < 3) {
        error = "Minimum 3 characters required";
      } else if (value.trim().length > 100) {
        error = "Maximum 100 characters allowed";
      }
    } else if (name === "category") {
      if (!value) {
        error = "Please select a category";
      }
    } else if (name === "durationDays") {
      if (!/^\d*$/.test(value)) {
        return;
      } else if (value === "") {
        error = "Enter number of days";
      } else if (Number(value) <= 0) {
        error = "Days must be greater than 0";
      }
    } else if (name === "durationNights") {
      if (!/^\d*$/.test(value)) {
        return;
      } else if (value === "") {
        error = "Enter number of nights";
      } else if (Number(value) < 0) {
        error = "Nights cannot be negative";
      }
    } else if (name === "basePax") {
      if (!value) {
        error = "Enter base pax";
      } else if (!/^\d+$/.test(value)) {
        error = "Only numbers allowed";
      } else if (Number(value) <= 0) {
        error = "Base pax must be greater than 0";
      }
    } else if (name === "description") {
      const clean = value.replace(/<[^>]*>/g, "").trim();

      if (clean === "") {
        error = "Please enter description";
      } else if (clean.length < 3) {
        error = "Minimum 3 characters required";
      }
    } else if (name === "inclusions") {
      const clean = value.replace(/<[^>]*>/g, "").trim();
      if (clean === "") {
        error = "Please enter inclusions";
      } else if (clean.length < 3) {
        error = "Minimum 3 characters required";
      }
    } else if (name === "paymentpolicy") {
      const clean = value.replace(/<[^>]*>/g, "").trim();
      if (clean === "") {
        error = "Please enter payment policy";
      } else if (clean.length < 3) {
        error = "Minimum 3 characters required";
      }
    } else if (name === "exclusions") {
      const clean = value.replace(/<[^>]*>/g, "").trim();

      if (!value || clean === "") {
        error = "Please enter exclusions";
      }
    } else if (name === "places") {
      if (!value || value.length === 0) {
        error = "Select at least one place";
      }
    }

    setForm({
      ...form,
      [name]: value,
    });

    setErrors({
      ...errors,
      [name]: error,
    });
  };

  const validateForm = () => {
    let newErrors = {};

    if (!form.title.trim()) {
      newErrors.title = "Please enter package name";
    }

    if (!form.category) {
      newErrors.category = "Please select a category";
    }

    if (!form.places || form.places.length === 0) {
      newErrors.places = "Select at least one place";
    }

    if (!form.durationDays || Number(form.durationDays) <= 0) {
      newErrors.durationDays = "Enter valid number of days";
    }

    if (!form.durationNights || Number(form.durationNights) < 0) {
      newErrors.durationNights = "Enter valid number of nights";
    }

    if (!form.basePax || Number(form.basePax) <= 0) {
      newErrors.basePax = "Enter valid base pax";
    }

    const cleanDescription = form.description.replace(/<[^>]*>/g, "").trim();
    if (!cleanDescription) {
      newErrors.description = "Please enter description";
    }

    const cleanInclusions = form.inclusions.replace(/<[^>]*>/g, "").trim();
    if (!cleanInclusions) {
      newErrors.inclusions = "Please enter inclusions";
    }

    const cleanPaymentpolicy = form.paymentpolicy
      .replace(/<[^>]*>/g, "")
      .trim();
    if (!cleanPaymentpolicy) {
      newErrors.paymentpolicy = "Please enter payment policy";
    }

    const cleanExclusions = form.exclusions.replace(/<[^>]*>/g, "").trim();
    if (!cleanExclusions) {
      newErrors.exclusions = "Please enter exclusions";
    }
    if (
      (!images || images.length === 0) &&
      (!form.image || form.image.length === 0)
    ) {
      newErrors.images = "Upload at least one image";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      // Upload all new images
      let uploadedUrls = [];

      if (images.length > 0) {
        for (const img of images) {
          const url = await uploadImage(img.file, token);
          if (url) uploadedUrls.push(url);
        }
      }

      // Merge old + new images
      const finalImages = [
        ...(Array.isArray(form.image) ? form.image : []),
        ...uploadedUrls,
      ];

      const basePayload = {
        title: form.title,
        places: form.places,
        category: form.category,
        durationDays: Number(form.durationDays),
        durationNights: Number(form.durationNights),
        pricingType: "dynamic",
        basePax: Number(form.basePax),
        description: form.description,
        image: finalImages,
        inclusions: form.inclusions,
        exclusions: form.exclusions,
        paymentpolicy: form.paymentpolicy,
      };
      if (packageId) {
        // UPDATE PAYLOAD (FLAT STRUCTURE)
        const updatePayload = {
          id: packageId,
          ...basePayload,
        };

        await dispatch(editpackage(updatePayload)).unwrap();
        toast.success("Package updated successfully");
      } else {
        console.log(
          "BASE PAYLOAD",
          basePayload
        );
        const res = await dispatch(addpackage(basePayload)).unwrap();

        console.log(
          "ADD PACKAGE RESPONSE",
          res
        );

        const id =
          res?.data?.package?._id ||
          res?.package?._id ||
          res?.data?._id ||
          res?._id ||
          null;
        const savedImages = res?.image || res?.data?.image || finalImages;
        if (!id) {
          toast.error("Package ID not found");
          return;
        }

        setPackageId(id);

        setForm((prev) => ({
          ...prev,
          image: savedImages,
        }));

        toast.success(
          "Package created successfully"
        );

        setDurationDays(
          form.durationDays
        );

        setTimeout(() => {
          nextStep();
        }, 100);

        return;
      }

      setDurationDays(form.durationDays);
      nextStep();
    } catch (error) {

      console.log(
        "PACKAGE CREATE ERROR",
        error
      );

      toast.error(
        error?.message ||
        error?.payload?.message ||
        error ||
        "Something went wrong"
      );
    }
  };

  const editorConfig = {
    toolbar: [
      "undo",
      "redo",
      "|",
      "heading",
      "|",
      "bold",
      "italic",
      "bulletedList",
      "numberedList",
      "|",
      "indent",
      "outdent",
      "blockQuote",
    ],
  };

  return (
    <Container>
      <Card className="mb-4 mt-2 p-4" style={{ border: "none" }}>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="g-3">
              <Col md={6}>
                <Form.Label>Package Name</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={form.title}
                  placeholder="Enter package name"
                  onChange={handleChange}
                  maxLength={200}
                  minLength={3}
                />

                {errors.title && (
                  <small className="text-danger">{errors.title}</small>
                )}
              </Col>

              <Col md={6}>
                <Form.Label>Package Category</Form.Label>

                <Form.Select
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                >
                  <option value="">Select Category</option>
                  {packageCategoryList.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.name}
                    </option>
                  ))}
                </Form.Select>

                {errors.category && (
                  <small className="text-danger mt-1">{errors.category}</small>
                )}
              </Col>

              <Col md={12}>
                <Form.Label>Select Locations</Form.Label>

                <Select
                  isMulti
                  options={placeOptions}
                  value={placeOptions.filter((option) =>
                    form.places?.includes(option.value),
                  )}
                  onChange={(selectedOptions) => {
                    const values = selectedOptions
                      ? selectedOptions.map((option) => option.value)
                      : [];

                    setForm((prev) => ({
                      ...prev,
                      places: values,
                    }));

                    setErrors((prev) => ({
                      ...prev,
                      places:
                        values.length === 0
                          ? "Please select at least one place"
                          : "",
                    }));
                  }}
                />

                {errors.places && (
                  <div className="text-danger mt-1">{errors.places}</div>
                )}
              </Col>

              <Col md={6}>
                <Form.Label>Duration Days</Form.Label>
                <Form.Control
                  type="text"
                  name="durationDays"
                  placeholder="Enter no. of days"
                  value={form.durationDays}
                  onChange={handleChange}
                  maxLength={2}
                  minLength={1}
                  disabled={!!packageId}
                />
                {errors.durationDays && (
                  <small className="text-danger">{errors.durationDays}</small>
                )}
              </Col>

              <Col md={6}>
                <Form.Label>Duration Nights</Form.Label>
                <Form.Control
                  type="text"
                  name="durationNights"
                  placeholder="Enter no. of nights"
                  value={form.durationNights}
                  onChange={handleChange}
                  maxLength={2}
                  minLength={1}
                  disabled={!!packageId}
                />
                {errors.durationNights && (
                  <small className="text-danger">{errors.durationNights}</small>
                )}
              </Col>

              <Col md={12}>
                <Form.Label>Description</Form.Label>

                <CKEditor
                  editor={ClassicEditor}
                  data={form.description || ""}
                  config={editorConfig}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    setForm((prev) => ({
                      ...prev,
                      description: data,
                    }));
                  }}
                />

                {errors.description && (
                  <div className="text-danger mt-1">{errors.description}</div>
                )}
              </Col>

              <Col md={12}>
                <Form.Label>Inclusions</Form.Label>

                <CKEditor
                  editor={ClassicEditor}
                  //  key={form.inclusions || "inclusions"}
                  data={form.inclusions || ""}
                  config={editorConfig}
                  onBlur={(event, editor) => {
                    const data = editor.getData();
                    setForm({ ...form, inclusions: data });
                  }}
                  onReady={(editor) => {
                    editor.editing.view.change((writer) => {
                      const root = editor.editing.view.document.getRoot();
                      writer.setStyle("height", "150px", root);
                      writer.setStyle("overflow-y", "auto", root);
                    });
                  }}
                  onChange={(event, editor) =>
                    handleChange({
                      target: { name: "inclusions", value: editor.getData() },
                    })
                  }
                />

                {errors.inclusions && (
                  <div className="text-danger mt-1">{errors.inclusions}</div>
                )}
              </Col>

              <Col md={12}>
                <Form.Label>Exclusions</Form.Label>

                <CKEditor
                  //   key={form.exclusions || "exclusions"}
                  editor={ClassicEditor}
                  data={form.exclusions || ""}
                  config={editorConfig}
                  onBlur={(event, editor) => {
                    const data = editor.getData();
                    setForm({ ...form, exclusions: data });
                  }}
                  onReady={(editor) => {
                    editor.editing.view.change((writer) => {
                      const root = editor.editing.view.document.getRoot();
                      writer.setStyle("height", "150px", root);
                      writer.setStyle("overflow-y", "auto", root);
                    });
                  }}
                  onChange={(event, editor) => {
                    const content = editor.getData();

                    setForm((prev) => ({
                      ...prev,
                      exclusions: content,
                    }));

                    const plainText = content
                      ?.replace(/<[^>]*>/g, "")
                      .replace(/&nbsp;/g, "")
                      .trim();

                    setErrors((prev) => ({
                      ...prev,
                      exclusions: plainText ? "" : "Please enter exclusions",
                    }));
                  }}
                />

                {errors.exclusions && (
                  <div className="text-danger mt-1">{errors.exclusions}</div>
                )}
              </Col>

              <Col md={12}>
                <Form.Label>Payment Policy</Form.Label>

                <CKEditor
                  editor={ClassicEditor}
                  data={form.paymentpolicy || ""}
                  config={editorConfig}
                  onBlur={(event, editor) => {
                    const data = editor.getData();
                    setForm({ ...form, paymentpolicy: data });
                  }}
                  onReady={(editor) => {
                    editor.editing.view.change((writer) => {
                      const root = editor.editing.view.document.getRoot();
                      writer.setStyle("height", "150px", root);
                      writer.setStyle("overflow-y", "auto", root);
                    });
                  }}
                  onChange={(event, editor) =>
                    handleChange({
                      target: {
                        name: "paymentpolicy",
                        value: editor.getData(),
                      },
                    })
                  }
                />

                {errors.paymentpolicy && (
                  <div className="text-danger mt-1">{errors.paymentpolicy}</div>
                )}
              </Col>

              <Col md={12}>
                <Form.Label className="fw-semibold mb-2">
                  Package Images
                </Form.Label>

                <div className="image-upload-wrapper">
                  <label htmlFor="packageImages" className="upload-box">
                    <div className="upload-content">
                      <div className="upload-icon">📷</div>
                      <p className="mb-1">Click or Drag Images to Upload</p>
                      <span className="text-muted small">
                        PNG, JPG • Multiple images allowed
                      </span>
                    </div>
                  </label>

                  <input
                    type="file"
                    id="packageImages"
                    multiple
                    accept="image/*"
                    className="file-input"
                    onChange={handleImageUpload}
                  />
                </div>

                {errors?.images && (
                  <div className="text-danger mt-2 small">{errors.images}</div>
                )}

                {(images.length > 0 ||
                  (form.image && form.image.length > 0)) && (
                    <div className="preview-grid">
                      {/* Existing Images */}
                      {Array.isArray(form.image) &&
                        form.image.map((imgUrl, index) => (
                          <div key={`existing-${index}`} className="preview-item">
                            <img src={imgUrl} alt="existing" />
                            <button
                              type="button"
                              className="remove-btn"
                              onClick={() => {
                                const updated = form.image.filter(
                                  (_, i) => i !== index,
                                );
                                setForm((prev) => ({
                                  ...prev,
                                  image: updated,
                                }));
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        ))}

                      {/* New Images */}
                      {images.map((imgObj, index) => (
                        <div key={`new-${index}`} className="preview-item">
                          <img src={imgObj.preview} alt="preview" />
                          <button
                            type="button"
                            className="remove-btn"
                            onClick={() => removeImage(index)}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
              </Col>
            </Row>

            <div className="step-footer">
              <div className="step-footer-right">
                <button type="submit" className="submit-btn px-4">
                  Next
                </button>
              </div>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
}
